import React, { useCallback, useState } from "react"
import { useHookstate } from "@hookstate/core"
import { Col, Row } from "react-bootstrap"
import { useExhibitorDataState } from "../../globalStates/ExhibitorDataState"
import { useLanguageState } from "../../globalStates/LanguageState"
import { useConfigDataState } from "../../globalStates/ConfigDataState"
import { hasServiceKeys } from "../../globalStates/ConfigDataState"
import FilePicker from "../../components/FilePicker"
import Section from "../../components/Section"
import EventCloudProductsPicker from "./EventCloudProductsPicker"
import { EventCloudProduct } from "../../branding/branding"

const LogoBackgroundImageSection: React.FC = () => {
    const branding = useLanguageState().getStrings()
    const exiState = useExhibitorDataState()
    const organizationState = exiState.getOrganization()
    const organization = useHookstate(organizationState)
    const serviceKeys = useConfigDataState().getServiceKeys()
    const logoVisible = hasServiceKeys(serviceKeys.logo)
    const headerGraphicVisible = hasServiceKeys(serviceKeys.headerGraphic)

    const options = branding.configuration.eventCloudProducts.list
    const [selectedOption, setSelectedOption] = useState<EventCloudProduct | null>(getInitialSelectedOption())

    function getInitialSelectedOption(): EventCloudProduct | null {
        return options.length > 0 ? options[0] : null
    }

    const onChangeLogo = useCallback(
        (
            image: File,
            onUploadProgress: (progress: number) => void,
            onSuccessCallback: () => void,
            onErrorCallback: (reason: string) => void
        ) => {
            exiState
                .upload("LOGO", image, onUploadProgress)
                .then((response) => {
                    onSuccessCallback()
                    if (response) {
                        organization.logoFileId.set(response.fileId)
                        organization.logoUrl.set(response.fileUrl)
                    }
                })
                .catch((reason) => onErrorCallback(reason))
        },
        [exiState, organization]
    )

    const onChangeDesktopBackground = useCallback(
        (
            image: File,
            onUploadProgress: (progress: number) => void,
            onSuccessCallback: () => void,
            onErrorCallback: (reason: string) => void
        ) => {
            exiState
                .upload("BACKGROUND_IMAGE", image, onUploadProgress)
                .then((response) => {
                    onSuccessCallback()
                    if (response) {
                        organization.backgroundImageFileId.set(response.fileId)
                        organization.backgroundImageUrl.set(response.fileUrl)
                    }
                })
                .catch((reason) => onErrorCallback(reason))
        },
        [exiState, organization]
    )

    const onChangeMobileBackground = useCallback(
        (
            image: File,
            onUploadProgress: (progress: number) => void,
            onSuccessCallback: () => void,
            onErrorCallback: (reason: string) => void
        ) => {
            exiState
                .upload("BACKGROUND_MOBILE", image, onUploadProgress)
                .then((response) => {
                    onSuccessCallback()
                    if (response) {
                        organization.backgroundImageMobileFileId.set(response.fileId)
                        organization.backgroundImageMobileUrl.set(response.fileUrl)
                    }
                })
                .catch((reason) => onErrorCallback(reason))
        },
        [exiState, organization]
    )

    const onRemoveLogo = useCallback(() => {
        organization.logoFileId.set("")
        organization.logoUrl.set("")
    }, [organization])

    const onRemoveDesktopBackground = useCallback(() => {
        organization.backgroundImageFileId.set("")
        organization.backgroundImageUrl.set("")
    }, [organization])

    const onRemoveMobileBackground = useCallback(() => {
        organization.backgroundImageMobileFileId.set("")
        organization.backgroundImageMobileUrl.set("")
    }, [organization])

    return (
        <Section id="companyLogoBackground" title={branding.configuration.profilePage.sctUpload.lblUpload}>
            <Row>
                {logoVisible && (
                    <Col sm={12} md={3}>
                        <FilePicker
                            name="companyLogo"
                            label={branding.configuration.profilePage.sctUpload.lblCompanyLogo}
                            placeholderTitle={branding.configuration.profilePage.sctUpload.hntTitleCompanyLogo}
                            placeholderText={branding.configuration.profilePage.sctUpload.hntPropCompanyLogo}
                            browseLabel={branding.configuration.profilePage.sctUpload.btnBrowseFilesLogo}
                            deleteLabel={branding.configuration.profilePage.sctUpload.btnDeleteImgLogo}
                            value={organization.logoUrl.get()}
                            onChangeImage={onChangeLogo}
                            onRemoveImage={onRemoveLogo}
                        />
                    </Col>
                )}
                {headerGraphicVisible && (
                    <Col sm={options.length > 1 ? 9 : 12} md={options.length > 1 ? 6 : 9}>
                        {((selectedOption === EventCloudProduct.EVENT_GUIDE ||
                            selectedOption === EventCloudProduct.WEB_WIDGETS) && (
                            <FilePicker
                                name="desktopBackground"
                                label={branding.configuration.profilePage.sctUpload.lblCompanyBackgroundImage}
                                placeholderTitle={branding.configuration.profilePage.sctUpload.hntTitleDesktopBackgroundImage}
                                placeholderText={branding.configuration.profilePage.sctUpload.hntPropDesktopBackgroundImage}
                                browseLabel={branding.configuration.profilePage.sctUpload.btnBrowseFilesBackgroundImage}
                                deleteLabel={branding.configuration.profilePage.sctUpload.btnDeleteImgBackgroundImage}
                                value={organization.backgroundImageUrl.get()}
                                onChangeImage={onChangeDesktopBackground}
                                onRemoveImage={onRemoveDesktopBackground}
                            />
                        )) ||
                            (selectedOption === EventCloudProduct.MOBILE_GUIDE && (
                                <FilePicker
                                    name="mobileBackground"
                                    label={branding.configuration.profilePage.sctUpload.lblCompanyBackgroundImage}
                                    placeholderTitle={branding.configuration.profilePage.sctUpload.hntTitleMobileBackgroundImage}
                                    placeholderText={branding.configuration.profilePage.sctUpload.hntPropMobileBackgroundImage}
                                    browseLabel={branding.configuration.profilePage.sctUpload.btnBrowseFilesBackgroundImage}
                                    deleteLabel={branding.configuration.profilePage.sctUpload.btnDeleteImgBackgroundImage}
                                    value={organization.backgroundImageMobileUrl.get()}
                                    onChangeImage={onChangeMobileBackground}
                                    onRemoveImage={onRemoveMobileBackground}
                                />
                            ))}
                    </Col>
                )}
                {headerGraphicVisible && options.length > 1 && (
                    <Col sm={3} md={3}>
                        <EventCloudProductsPicker selectedOption={selectedOption} setSelectedOption={setSelectedOption} />
                    </Col>
                )}
            </Row>
        </Section>
    )
}

export default LogoBackgroundImageSection
